import React, { useState, useEffect } from 'react';
import { styled } from 'styletron-react';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Plus, Trash2, ChevronDown, ChevronRight, ToggleLeft, ToggleRight, Type, Hash, Braces } from 'lucide-react';
import { toaster } from 'baseui/toast';

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
});

const PropertyItem = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 0',
  borderBottom: '1px solid #F3F4F6',
  ':last-child': { borderBottom: 'none' },
});

const PropertyKey = styled('div', {
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  minWidth: '140px',
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const PropertyValue = styled('div', {
  flex: 1,
  minWidth: 0,
});

const ObjectPreview = styled('div', {
  fontSize: '12px',
  color: '#9CA3AF',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 10px',
  backgroundColor: '#F9FAFB',
  borderRadius: '6px',
  border: '1px solid #E5E7EB',
  ':hover': { backgroundColor: '#F3F4F6' },
});

const ObjectExpanded = styled('div', {
  backgroundColor: '#F9FAFB',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  padding: '12px',
  marginTop: '4px',
});

const BoolToggle = styled('div', ({ $active }: { $active: boolean }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
  backgroundColor: $active ? '#D1FAE5' : '#FEE2E2',
  color: $active ? '#065F46' : '#991B1B',
  transition: 'all 0.15s ease',
  userSelect: 'none',
}));

const AddRow = styled('div', {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  padding: '12px 0',
  borderTop: '1px solid #E5E7EB',
  marginTop: '8px',
});

const EmptyState = styled('div', {
  textAlign: 'center',
  padding: '24px',
  color: '#9CA3AF',
  fontSize: '13px',
});

const DeleteBtn = styled('button', {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '4px',
  color: '#D1D5DB',
  display: 'flex',
  alignItems: 'center',
  ':hover': { color: '#EF4444', backgroundColor: '#FEF2F2' },
});

interface JsonSettingsEditorProps {
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  disabled?: boolean;
}

// Detect value type for display
const getValueType = (val: any): 'boolean' | 'number' | 'object' | 'string' => {
  if (typeof val === 'boolean') return 'boolean';
  if (typeof val === 'number') return 'number';
  if (typeof val === 'object' && val !== null) return 'object';
  return 'string';
};

const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const size = 12;
  const color = '#9CA3AF';
  switch (type) {
    case 'boolean': return <ToggleLeft size={size} color={color} />;
    case 'number': return <Hash size={size} color={color} />;
    case 'object': return <Braces size={size} color={color} />;
    default: return <Type size={size} color={color} />;
  }
};

// Format key for display: snake_case → Title Case
const formatKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
};

export const JsonSettingsEditor: React.FC<JsonSettingsEditorProps> = ({
  value = {},
  onChange,
  disabled = false,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  // Filter out known structured settings (like hikConnect) — they have their own UI
  const knownKeys = new Set(['hikConnect']);
  const entries = Object.entries(value).filter(([k]) => !knownKeys.has(k));

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const updateValue = (key: string, newVal: any) => {
    onChange({ ...value, [key]: newVal });
  };

  const deleteKey = (key: string) => {
    const { [key]: _, ...rest } = value;
    onChange(rest);
  };

  const addProperty = () => {
    if (!newKey.trim()) {
      toaster.warning('Key cannot be empty', { autoHideDuration: 2000 });
      return;
    }
    if (Object.prototype.hasOwnProperty.call(value, newKey)) {
      toaster.warning('Key already exists', { autoHideDuration: 2000 });
      return;
    }

    let parsed: any = newValue;
    try { parsed = JSON.parse(newValue); } catch {}

    onChange({ ...value, [newKey]: parsed });
    setNewKey('');
    setNewValue('');
  };

  const renderValue = (key: string, val: any) => {
    const type = getValueType(val);

    if (type === 'boolean') {
      return (
        <BoolToggle
          $active={val}
          onClick={() => !disabled && updateValue(key, !val)}
        >
          {val ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
          {val ? 'true' : 'false'}
        </BoolToggle>
      );
    }

    if (type === 'object') {
      const isExpanded = expandedKeys.has(key);
      const preview = Array.isArray(val) ? `[${val.length} items]` : `{${Object.keys(val).length} keys}`;
      return (
        <div>
          <ObjectPreview onClick={() => toggleExpand(key)}>
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {preview}
          </ObjectPreview>
          {isExpanded && (
            <ObjectExpanded>
              <Input
                value={JSON.stringify(val, null, 2)}
                onChange={(e) => {
                  try {
                    updateValue(key, JSON.parse((e.target as HTMLInputElement).value));
                  } catch {}
                }}
                size="compact"
                disabled={disabled}
                overrides={{
                  Input: { style: { fontFamily: 'monospace', fontSize: '12px' } },
                }}
              />
            </ObjectExpanded>
          )}
        </div>
      );
    }

    // String or number
    return (
      <Input
        value={String(val)}
        onChange={(e) => {
          const raw = (e.target as HTMLInputElement).value;
          if (type === 'number') {
            const num = Number(raw);
            updateValue(key, isNaN(num) ? raw : num);
          } else {
            updateValue(key, raw);
          }
        }}
        size="compact"
        disabled={disabled}
        overrides={{
          Root: { style: { backgroundColor: 'transparent' } },
        }}
      />
    );
  };

  return (
    <Container>
      {entries.length === 0 && (
        <EmptyState>No custom settings configured yet</EmptyState>
      )}

      {entries.map(([key, val]) => (
        <PropertyItem key={key}>
          <PropertyKey title={key}>
            <TypeIcon type={getValueType(val)} />
            {formatKey(key)}
          </PropertyKey>
          <PropertyValue>
            {renderValue(key, val)}
          </PropertyValue>
          <DeleteBtn onClick={() => !disabled && deleteKey(key)} title="Remove">
            <Trash2 size={14} />
          </DeleteBtn>
        </PropertyItem>
      ))}

      <AddRow>
        <Input
          value={newKey}
          onChange={(e) => setNewKey((e.target as HTMLInputElement).value)}
          placeholder="New key"
          size="compact"
          disabled={disabled}
          onKeyDown={(e) => e.key === 'Enter' && addProperty()}
          overrides={{ Root: { style: { flex: '0 0 180px' } } }}
        />
        <Input
          value={newValue}
          onChange={(e) => setNewValue((e.target as HTMLInputElement).value)}
          placeholder="Value (text, number, true/false, or JSON)"
          size="compact"
          disabled={disabled}
          onKeyDown={(e) => e.key === 'Enter' && addProperty()}
          overrides={{ Root: { style: { flex: 1 } } }}
        />
        <Button
          onClick={addProperty}
          disabled={disabled || !newKey.trim()}
          size="compact"
          kind="secondary"
          overrides={{
            BaseButton: { style: { borderRadius: '8px', padding: '8px' } },
          }}
        >
          <Plus size={16} />
        </Button>
      </AddRow>
    </Container>
  );
};
